import React from 'react';
import styled from 'styled-components/macro';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import * as colors from 'src/styles/colors';
import { scrollbarStyle } from 'src/styles/scrollbar';
import { PrimaryColors } from 'src/types/globalTypes';

// 必須引入才有樣式
import 'react-datepicker/dist/react-datepicker.css';
// 顯示中文
import zhTW from 'date-fns/locale/zh-TW';
import { mobilePoint } from 'src/styles/globalStyle';
registerLocale('zhTW', zhTW);

const IconLabel = styled.label<{ width?: string }>`
  width: ${(props) => props.width};
  display: flex;
  align-items: center;
  position: relative;

  svg {
    position: absolute;
    right: 12px;
    font-size: 16px;
    :hover {
      cursor: pointer;
    }
    z-index: 1;
  }
`;

const Label = styled(IconLabel)<{ primaryColors?: PrimaryColors; isCustomized: boolean }>`
  input {
    position: relative;
    width: 100%;
    height: 40px;
    padding: ${(props) => (props.isCustomized ? '25px 0px 5px 13px' : '5px 0px 5px 16px')};
    border-radius: 4px;
    border: solid 1px
      ${(props) => (props.primaryColors ? props.primaryColors.third : colors.primaryBlue_100)};
    background-color: ${colors.white};

    @media (max-width: ${mobilePoint}) {
      height: 57px;
    }

    ::placeholder {
      color: ${colors.BlueGray_200};
    }

    :focus {
      outline: none;
      border: 1px solid
        ${(props) => (props.primaryColors ? props.primaryColors.sixth : colors.primaryBlue_500)};
    }
  }

  .react-datepicker {
    font-family: NotoSansSC-Light, 'Microsoft JhengHei', Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
  }
  .react-datepicker__current-month,
  .react-datepicker-time__header,
  .react-datepicker-year-header {
    margin-bottom: 8px;
    line-height: 1em;
  }

  /* DatePicker 會自己在input外產生兩個div，必須強制給寬度 */
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    width: 100%;
  }
  .react-datepicker-popper {
    z-index: 2;
  }

  .react-datepicker__header__dropdown {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    line-height: 1em;
  }

  .react-datepicker__year-dropdown--scrollable,
  .react-datepicker__month-dropdown--scrollable,
  .react-datepicker__month-year-dropdown--scrollable {
    ${scrollbarStyle}
  }

  /**
    Open issue: year drop down header arrow not showing up
    https://github.com/Hacker0x01/react-datepicker/issues/3113
    Styles for workaround
  */
  .react-datepicker__navigation--years {
    background: none;
    line-height: 1.7rem;
    text-align: center;
    cursor: pointer;
    padding: 0;
    border: 0.45rem solid transparent;
    z-index: 1;
    height: 10px;
    width: 10px;
    text-indent: -999em;
    overflow: hidden;
    position: relative;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }

  .react-datepicker__navigation--years.react-datepicker__navigation--years-upcoming {
    top: -4px;
    border-bottom-color: #ccc;
  }

  .react-datepicker__navigation--years.react-datepicker__navigation--years-previous {
    top: 4px;
    border-top-color: #ccc;
  }

  .react-datepicker__year-option {
    :hover {
      .react-datepicker__navigation--years.react-datepicker__navigation--years-upcoming {
        border-bottom-color: #a6a6a6;
      }
      .react-datepicker__navigation--years.react-datepicker__navigation--years-previous {
        border-top-color: #a6a6a6;
      }
    }
  }
`;

const CustomizedPlaceholderTitle = styled.span`
  position: absolute;
  pointer-events: none;
  left: 15px;
  top: -2px;
  font-size: 12px;
  color: ${colors.BlueGray_500};
`;

const StyledHiddenLabel = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;



interface IProps {
  onChange: (date: Date | null, event: React.SyntheticEvent<any, Event> | undefined) => void;
  selected?: Date | null;
  width?: string;
  primaryColors?: PrimaryColors;
  placeholderTitle?: string;
  datePickerId?: string;
}

const DatePicker: React.FC<IProps> = (props) => {
  return (
    <Label
      width={props.width}
      primaryColors={props.primaryColors}
      isCustomized={!!props.placeholderTitle}
    >
	  <StyledHiddenLabel>
	    {props.placeholderTitle || "輸入日期欄位"}
	  </StyledHiddenLabel>
      <ReactDatePicker
        placeholderText="年-月-日"
        dateFormat="yyyy-MM-dd"
        locale={zhTW}
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={5}
        selected={props.selected}
        onChange={(date, e) => {
          e?.preventDefault();
          props.onChange(date, e);
        }}
        onChangeRaw={(e) => e.preventDefault()}
        onFocus={(e) => e.target.blur()}
		id={props.datePickerId}
      />
      {props.placeholderTitle && (
        <CustomizedPlaceholderTitle>{props.placeholderTitle}</CustomizedPlaceholderTitle>
      )}
    </Label>
  );
};

export default DatePicker;
